import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Field, fieldInputCls } from "@/components/Field";
import { FileField } from "@/components/FileField";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useSettings } from "@/context/SettingsContext";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
  first_name: "", photo: null, last_name: "", address: "", email: "", phone: "", social: "", birth_date: "", category: "",
  license_number: "", license_file: null, medical_date: "", medical_expiry: "", medical_file: null, chassis_notes: "", notes: "",
};

const Section = ({ title, children }) => (
  <div className="space-y-3 border-t border-[#282D3D] pt-4">
    <div className="font-heading uppercase font-bold text-lg flex items-center gap-2"><span className="w-1 h-4 bg-[#FF3B30]" />{title}</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

export const DriverDialog = ({ open, onOpenChange, driver, onSaved }) => {
  const { t } = useT();
  const { settings } = useSettings();
  const [form, setForm] = useState(EMPTY);
  useEffect(() => {
    if (open) {
      setForm(driver ? { ...EMPTY, ...driver } : EMPTY);
    }
  }, [open, driver]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inp = (k, label, type = "text", span = "") => (
    <Field label={label} className={span}>
      <Input data-testid={`driver-${k}-input`} type={type} value={form[k] ?? ""} onChange={set(k)} className={fieldInputCls} />
    </Field>
  );
  const area = (k, label) => (
    <Field label={label} className="sm:col-span-2">
      <Textarea data-testid={`driver-${k}-input`} rows={3} value={form[k] ?? ""} onChange={set(k)} className={`${fieldInputCls} h-auto font-sans`} />
    </Field>
  );

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    ["id", "user_id", "created_at"].forEach((k) => delete payload[k]);
    try {
      if (driver) await api.put(`/drivers/${driver.id}`, payload);
      else await api.post("/drivers", payload);
      toast.success(t("saved"));
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    await api.delete(`/drivers/${driver.id}`);
    toast.success(t("deleted"));
    onSaved();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="driver-dialog" className="bg-[#12141C] border-[#282D3D] rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-2xl">{driver ? t("edit_driver") : t("new_driver")}</DialogTitle>
          <DialogDescription className="sr-only">{t("drivers_sub")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <Section title={t("personal")}>
            <Field label={t("photo")} className="sm:col-span-2">
              <FileField testid="driver-photo" preview accept="image/jpeg,image/png,image/webp,image/gif" value={form.photo} onChange={(v) => setForm((f) => ({ ...f, photo: v }))} />
            </Field>
            <Field label={`${t("first_name")} *`}>
              <Input data-testid="driver-first_name-input" required value={form.first_name} onChange={set("first_name")} className={fieldInputCls} />
            </Field>
            {inp("last_name", t("last_name"))}
            {inp("birth_date", t("birth_date"), "date")}
            {!settings.hide_categories && inp("category", t("category"))}
            {inp("address", t("address"), "text", "sm:col-span-2")}
          </Section>
          <Section title={t("contacts")}>
            {inp("email", t("email"), "email")}
            {inp("phone", t("phone"), "tel")}
            {inp("social", t("social"), "text", "sm:col-span-2")}
          </Section>
          <Section title={t("documents")}>
            {inp("license_number", t("license_number"))}
            <Field label={`${t("license")} · PDF / foto`}>
              <FileField testid="driver-license-file" value={form.license_file} onChange={(v) => setForm((f) => ({ ...f, license_file: v }))} />
            </Field>
            {inp("medical_date", t("medical_date"), "date")}
            {inp("medical_expiry", t("medical_expiry"), "date")}
            <Field label={`${t("medical")} · PDF / foto`} className="sm:col-span-2">
              <FileField testid="driver-medical-file" value={form.medical_file} onChange={(v) => setForm((f) => ({ ...f, medical_file: v }))} />
            </Field>
          </Section>
          <Section title={t("notes")}>
            {area("chassis_notes", t("chassis_notes"))}
            {area("notes", t("misc_notes"))}
          </Section>
          <div className="flex items-center gap-2 pt-2">
            {driver && (
              <ConfirmDelete testid="driver-delete" onConfirm={remove} trigger={
                <Button type="button" variant="outline" data-testid="driver-delete-btn" className="rounded-sm bg-transparent border-[#282D3D] text-slate-400 hover:text-[#FF3B30] hover:border-[#FF3B30] hover:bg-transparent">
                  <Trash2 className="w-4 h-4" />
                </Button>
              } />
            )}
            <Button type="button" variant="ghost" className="ml-auto" data-testid="driver-cancel-btn" onClick={() => onOpenChange(false)}>{t("cancel")}</Button>
            <Button type="submit" data-testid="driver-save-btn" className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-base px-6">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
